import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useRefreshAccountOpeningStatusMutation } from '../../../store/accountOpening/accountOpening'
import { type AccountOpeningDTO, type AccountOpeningStatus } from '../../../models/AccountOpening'
import { type AccountOpeningDescriptor } from '../../../models/AccountOpeningDescriptor'
import { isTerminalStatus, storedStatusCode } from '../domain/connectorResult'

const _BATCH_SIZE = 10

const _UNSUBMITTED: AccountOpeningStatus[] = ['draft', 'ready']

const isRefreshable = (opening: AccountOpeningDTO, descriptor: AccountOpeningDescriptor | undefined): boolean =>
  !_UNSUBMITTED.includes(opening.status) && !isTerminalStatus(descriptor, storedStatusCode(opening.metadata))

const inBatches = async <T>(items: T[], size: number, run: (item: T) => Promise<unknown>): Promise<void> => {
  const batches = Array.from({ length: Math.ceil(items.length / size) }, (_, batch) => items.slice(batch * size, batch * size + size))
  for (const batch of batches) {
    await Promise.allSettled(batch.map(run))
  }
}

export const useOpeningStatusRefresh = (accountId: string, descriptor: AccountOpeningDescriptor | undefined, openings: AccountOpeningDTO[]) => {
  const [refreshAccountOpeningStatus] = useRefreshAccountOpeningStatusMutation()
  const askedFor = useRef(new Set<string>())
  const pending = useRef(new Set<string>())
  const [pendingIds, setPendingIds] = useState<string[]>([])
  const [failedIds, setFailedIds] = useState<string[]>([])

  const ask = useCallback(
    async (openingIds: string[]): Promise<void> => {
      // An overlapping call must not take over ids the in-flight one still owns, or its `finally` clears their loading state early.
      const ids = openingIds.filter((id) => !pending.current.has(id))
      if (ids.length === 0) {
        return
      }
      ids.forEach((id) => pending.current.add(id))
      setPendingIds([...pending.current])

      setFailedIds((failed) => failed.filter((id) => !ids.includes(id)))
      try {
        await inBatches(ids, _BATCH_SIZE, async (openingId) => {
          try {
            await refreshAccountOpeningStatus({ accountId, openingId }).unwrap()
          } catch (error) {
            setFailedIds((failed) => (failed.includes(openingId) ? failed : [...failed, openingId]))
            Sentry.captureException(error)
          }
        })
      } finally {
        ids.forEach((id) => pending.current.delete(id))
        setPendingIds([...pending.current])
      }
    },
    [accountId, refreshAccountOpeningStatus],
  )

  const refreshableKey = openings
    .filter((opening) => isRefreshable(opening, descriptor))
    .map((opening) => opening.id)
    .join(',')
  const refreshable = useMemo(() => (refreshableKey ? refreshableKey.split(',') : []), [refreshableKey])

  useEffect(() => {
    const unasked = refreshable.filter((id) => !askedFor.current.has(id))
    if (unasked.length === 0) {
      return
    }
    unasked.forEach((id) => askedFor.current.add(id))
    void ask(unasked)
  }, [refreshable, ask])

  return {
    isRefreshing: (openingId: string): boolean => pendingIds.includes(openingId),
    hasFailed: (openingId: string): boolean => failedIds.includes(openingId),
    isRefreshingAny: pendingIds.length > 0,
    refreshAllNow: useCallback(() => void ask(refreshable), [ask, refreshable]),
    hasRefreshable: refreshable.length > 0,
  }
}
